import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useInactivityLogout } from './hooks/useInactivityLogout';

// Pages
import LandingPage from "./pages/LandingPage";
import ForCompanyPage from "./pages/landing/ForCompanyPage";
import ForGymPage from "./pages/landing/ForGymPage";
import ForYouPage from "./pages/landing/ForYouPage";
import AuthCallback from "./pages/AuthCallback";
import SelectRole from "./pages/SelectRole";
import ResetPassword from "./pages/ResetPassword";
import CompanyPlansPage from "./pages/CompanyPlansPage";
import GymPlansPage from "./pages/GymPlansPage";
import StudentPlansPage from "./pages/StudentPlansPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import RHDashboard from "./pages/RHDashboard";
import GymDashboard from "./pages/GymDashboard";
import StudentDashboard from "./pages/StudentDashboard";
import ProfessionalDashboard from "./pages/ProfessionalDashboard";
import UpdateBanner from "./components/UpdateBanner";
import ErrorBoundary from "./components/ErrorBoundary";
import { I18nProvider } from "./i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/** Convert storage URLs/paths to proxied URLs for compatibility */
export const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  // Full object storage URL
  if (url.includes('emergentagent.com/objstore/')) {
    return `${API}/images/proxy?url=${encodeURIComponent(url)}`;
  }
  // Relative storage path (e.g., "vital-performance/gyms/...")
  if (url.startsWith('vital-performance/') || url.includes('/student-photos/') || url.includes('/gyms/') || url.includes('/company-logos/')) {
    return `${API}/images/proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
};

// Axios interceptor: attach Bearer token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Session token helpers
export const saveSessionToken = (token) => {
  if (token) localStorage.setItem('session_token', token);
};

export const clearSessionToken = () => {
  localStorage.removeItem('session_token');
  localStorage.removeItem('vital_last_activity');
};

// Auth Context
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch {
      setUser(null);
      clearSessionToken();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    if (user) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem('session_token');
    if (token) {
      checkAuth();
    } else {
      setLoading(false);
    }
  }, [checkAuth, user]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch { /* ignore */ }
    setUser(null);
    clearSessionToken();
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Inactivity guard: wraps authenticated routes
const InactivityGuard = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleInactivityLogout = useCallback(async () => {
    await logout();
    navigate('/', { replace: true });
  }, [logout, navigate]);

  // Ativar apenas para usuários logados
  useInactivityLogout(user ? handleInactivityLogout : () => {});

  return children;
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/" state={{ from: location }} replace />;
  if (!user.role) return <Navigate to="/select-role" replace />;

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    const dashboardMap = { rh: '/rh', gym: '/gym', student: '/student', professional: '/professional' };
    return <Navigate to={dashboardMap[user.role] || '/'} replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <InactivityGuard>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/para-empresa" element={<ForCompanyPage />} />
        <Route path="/para-academia" element={<ForGymPage />} />
        <Route path="/para-voce" element={<ForYouPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/select-role" element={<SelectRole />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/plans/company" element={<CompanyPlansPage />} />
        <Route path="/plans/gym" element={<GymPlansPage />} />
        <Route path="/plans/student" element={<StudentPlansPage />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/*" element={<AdminDashboard />} />
        <Route path="/rh/*" element={
          <ProtectedRoute allowedRoles={['rh']}>
            <RHDashboard />
          </ProtectedRoute>
        } />
        <Route path="/gym/*" element={
          <ProtectedRoute allowedRoles={['gym']}>
            <GymDashboard />
          </ProtectedRoute>
        } />
        <Route path="/student/*" element={
          <ProtectedRoute allowedRoles={['student']}>
            <StudentDashboard />
          </ProtectedRoute>
        } />
        <Route path="/professional/*" element={
          <ProtectedRoute allowedRoles={['professional']}>
            <ProfessionalDashboard />
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </InactivityGuard>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <I18nProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <UpdateBanner />
          </AuthProvider>
        </BrowserRouter>
      </I18nProvider>
    </ErrorBoundary>
  );
}

export default App;
